import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

const COOLDOWN_SEC = 60;

export default function EmailVerifyPrompt() {
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const resend = useCallback(async () => {
    if (sending || cooldown > 0) return;
    setSending(true);
    setMessage(null);
    setError(null);
    try {
      await api.post("/xapi/auth/email/resend", {}); // canonical single sender
      setMessage("Verification email sent. Please check your inbox.");
      setCooldown(COOLDOWN_SEC);
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Could not send verification email. Please try again.";
      setError(msg);
      setCooldown(10);
    } finally {
      setSending(false);
    }
  }, [sending, cooldown]);

  return (
    <div className="max-w-xl mx-auto p-6">
      <div className="section-box">
        <h1 className="text-xl font-semibold mb-2">Verify your email</h1>
        <p className="text-gray-600 mb-4">
          No email was sent automatically. Press the button below to send your verification link.
        </p>

        {message && (
          <div className="alert-success mb-3">
            <div className="font-medium">{message}</div>
          </div>
        )}
        {error && (
          <div className="alert-danger mb-3">
            <div className="font-medium">{error}</div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            className={`btn ${sending || cooldown > 0 ? "opacity-60 cursor-not-allowed" : ""}`}
            onClick={resend}
            disabled={sending || cooldown > 0}
            type="button"
          >
            {sending ? "Sending..." : cooldown > 0 ? `Resend in ${cooldown}s` : "Send verification email"}
          </button>

          <Link to="/dashboard" className="btn-white">
            Back to dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}
